import React, { useCallback, useEffect, useRef, useState } from "react";
import server from "api";
import {
  ChangelogEntry,
  ChangelogMedia,
  PublishedChangelogResponse,
} from "api/Changelog/publishedTypes";

const safeHttpsUrl = (value?: string): string | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
};

const formatPublishedAt = (publishedAt: number) => {
  const milliseconds =
    publishedAt < 10_000_000_000 ? publishedAt * 1000 : publishedAt;
  const date = new Date(milliseconds);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
};

const ChangelogMediaView = ({ media }: { media: ChangelogMedia }) => {
  const url = safeHttpsUrl(media.url);
  if (!url) return null;

  if (media.type === "video") {
    return (
      <video
        className="support-changelog__media"
        src={url}
        controls
        preload="metadata"
        playsInline
        aria-label={media.alt || "Video zur Neuigkeit"}
      />
    );
  }

  return (
    <img
      className="support-changelog__media"
      src={url}
      alt={media.alt || ""}
      loading="lazy"
    />
  );
};

const ChangelogView = () => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mountedRef = useRef(false);

  const loadEntries = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    setError(false);
    try {
      const { data } = await server.get<PublishedChangelogResponse>(
        "/v03/changelog",
        { params: { limit: 50 } }
      );
      if (!mountedRef.current) return;
      setEntries(
        Array.isArray(data.entries)
          ? data.entries.filter((entry) => entry.status === "published")
          : []
      );
    } catch {
      if (!mountedRef.current) return;
      setEntries([]);
      setError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void loadEntries();
    return () => {
      mountedRef.current = false;
    };
  }, [loadEntries]);

  if (loading) {
    return (
      <div className="support-changelog support-changelog--state">
        Neuigkeiten werden geladen …
      </div>
    );
  }

  if (error) {
    return (
      <div className="support-changelog support-changelog--state">
        <p>Die Neuigkeiten konnten nicht geladen werden.</p>
        <button
          type="button"
          className="support-changelog__retry"
          onClick={() => void loadEntries()}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!entries.length) {
    return (
      <div className="support-changelog support-changelog--state">
        Noch keine Neuigkeiten veröffentlicht.
      </div>
    );
  }

  return (
    <div className="support-changelog">
      <p className="support-changelog__intro">
        Produktneuigkeiten und Verbesserungen bei Gastronaut.
      </p>
      <div className="support-changelog__entries">
        {entries.map((entry) => (
          <article
            key={entry.id}
            className={`support-changelog__entry support-changelog__entry--priority-${entry.hierarchy}`}
          >
            <div className="support-changelog__meta">
              <span>Version {entry.version}</span>
              <span>{formatPublishedAt(entry.publishedAt)}</span>
            </div>
            <h3 className="support-changelog__title">{entry.title}</h3>
            <p className="support-changelog__body">{entry.body}</p>
            {entry.media && <ChangelogMediaView media={entry.media} />}
          </article>
        ))}
      </div>
    </div>
  );
};

export default ChangelogView;
